'use client';

import { useEffect, useRef } from 'react';

type Props = {
  children: React.ReactNode;
  /** Radius in px within which the magnet activates. */
  radius?: number;
  /** Maximum pixels the inner element translates toward the cursor. */
  strength?: number;
  className?: string;
};

// Magnetic-pull wrapper. The outer div catches the mousemove event over an
// expanded hit zone; the inner span translates a fraction of the cursor delta
// toward the center, creating the classic "button leans toward your finger"
// effect. Disabled for reduced-motion + touch users.
export function MagneticCta({ children, radius = 90, strength = 8, className }: Props) {
  const outerRef = useRef<HTMLSpanElement | null>(null);
  const innerRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    if (typeof window === 'undefined') return;
    const fine = window.matchMedia('(pointer: fine)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!fine.matches || motion.matches) return;

    let rafId = 0;
    let targetX = 0;
    let targetY = 0;
    let x = 0;
    let y = 0;

    function tick() {
      x += (targetX - x) * 0.18;
      y += (targetY - y) * 0.18;
      if (inner) {
        inner.style.transform = `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`;
      }
      rafId = requestAnimationFrame(tick);
    }

    function onMove(e: PointerEvent) {
      if (!outer) return;
      const rect = outer.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > radius) {
        targetX = 0;
        targetY = 0;
        return;
      }
      const pull = (1 - dist / radius) * strength;
      targetX = (dx / dist || 0) * pull;
      targetY = (dy / dist || 0) * pull;
    }

    function onLeave() {
      targetX = 0;
      targetY = 0;
    }

    window.addEventListener('pointermove', onMove, { passive: true });
    outer.addEventListener('pointerleave', onLeave);
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', onMove);
      outer.removeEventListener('pointerleave', onLeave);
      if (inner) inner.style.transform = '';
    };
  }, [radius, strength]);

  return (
    <span ref={outerRef} className={className} style={{ display: 'inline-flex' }}>
      <span ref={innerRef} style={{ display: 'inline-flex', transition: 'transform 100ms linear' }}>
        {children}
      </span>
    </span>
  );
}
